import clsx from 'clsx';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import {
  CriticalIcon,
  InformationIcon,
  SuccessIcon,
  WarningIcon,
} from '@dynatrace/strato-icons';

import { withFreeform } from '../../buttons/inline-button/InlineButton.sty.js';
import { InlineButton } from '../../buttons/inline-button/InlineButton.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { useInformationOverlayContext } from './hooks/useInformationOverlayContext.js';
import { COMPONENT_NAME } from './information-overlay-constants.js';
import * as styles from './InformationOverlay.sty.js';
import { InformationOverlayIcon } from './InformationOverlayIcon.js';

//#region Typings
/**
 * @public
 */
export interface InformationOverlayTriggerProps
  extends
    DOMProps,
    WithChildren,
    StylingProps,
    MaskingProps,
    DataTestId,
    BehaviorTrackingProps,
    AriaLabelingProps {}
//#endregion Typings

/**
 * InformationOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  InformationOverlayTriggerProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const { color, overlayTriggerProps, isOpen } = useInformationOverlayContext();
  const id = useId();

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const {
    'data-dt-name': dataDtName = 'Trigger',
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;
  const { 'aria-label': ariaLabelProp, ...remainingAriaLabelingProps } =
    useAriaLabelingProps(restProps);

  const [slots, freeform] = useSlots(children, {
    icon: InformationOverlayIcon,
  });
  const hasFreeform = freeform.some(
    (child) => child !== '' && child !== undefined && child !== null,
  );

  if (slots.icon === undefined) {
    switch (color) {
      case 'success':
        slots.icon = <SuccessIcon />;
        break;
      case 'warning':
        slots.icon = <WarningIcon />;
        break;
      case 'critical':
        slots.icon = <CriticalIcon />;
        break;
      default:
        slots.icon = <InformationIcon />;
        break;
    }
  }

  // If the trigger gets string children, then there is
  // 1. no need for an aria label
  // 2. The aria label would need to at least contain the parts of the children string based on https://accessibilityinsights.io/info-examples/web/needs-review/label-content-name-mismatch/
  const intl = useIntl();
  let ariaLabel = ariaLabelProp;

  if (
    ariaLabel === undefined &&
    (children === undefined || !_canReceiveDefaultTextFormat(children))
  ) {
    ariaLabel = intl.formatMessage({
      defaultMessage: 'Open additional information.',
      id: 'NXdoElYQZNKea1Sc',
      description: 'Aria label fallback for Information overlay trigger',
    });
  }

  return (
    <InlineButton
      as="button"
      ref={forwardedRef}
      {...overlayTriggerProps}
      color={hasFreeform ? 'neutral' : color}
      id={propId ?? id}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      data-state={isOpen ? 'open' : undefined}
      className={clsx(
        {
          [withFreeform]: hasFreeform,
        },
        consumerClassName,
      )}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-children-name={dataDtName}
      data-dt-component={COMPONENT_NAME}
      {...remainingAriaLabelingProps}
      {...remainingBehavioralTrackingProps}
    >
      <InlineButton.Prefix className={styles.prefixVariants[color]}>
        {slots.icon}
      </InlineButton.Prefix>
      {hasFreeform && (
        <span className={styles.freeformWrapper}>{freeform}</span>
      )}
    </InlineButton>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'InformationOverlay.Trigger';
